import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getQuickDevice } from "@/lib/quickAccess";
import { Logo } from "@/components/Logo";
import { FilePlus2, LayoutDashboard, ArrowRight } from "lucide-react";

/**
 * Pantalla intermedia tras acceso rápido con PIN (sección 9.10 PRD).
 * Solo la ve el admin. Si se llega aquí sin JWT, redirige a login.
 */
export default function Welcome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qd = getQuickDevice();
  const [greetingName] = useState(() => qd?.first_name || (user?.name || "").split(" ")[0] || "Administrador");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (user.role !== "admin") { navigate("/dashboard", { replace: true }); }
  }, [user, loading, navigate]);

  // Mostrar la UI apenas tengamos el nombre (que viene de localStorage) — no bloquear
  // la pantalla mientras corre refresh() en background. El useEffect arriba desviará
  // si al final la sesión resulta inválida.
  if (loading && !user && !qd) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#005A91] via-[#004a7a] to-[#003a60] text-white flex flex-col"
         data-testid="welcome-page">
      <div className="absolute inset-0 opacity-10 pointer-events-none"
           style={{
             backgroundImage: "url('https://images.unsplash.com/photo-1509390144018-eeaf65052242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')",
             backgroundSize: "cover", backgroundPosition: "center",
           }} />
      <div className="relative flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-3xl mx-auto text-center">
        <div className="mb-8">
          <Logo height={80} dark />
        </div>
        <div className="text-[11px] font-black tracking-[0.28em] uppercase text-[#4ECCC8]">Acceso rápido</div>
        <h1 className="mt-3 text-5xl sm:text-6xl font-black tracking-tight leading-[1.03]">
          Hola, {greetingName}.
        </h1>
        <p className="mt-4 text-lg sm:text-xl text-white/85 max-w-md">
          ¿Por dónde quieres empezar hoy?
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          <button
            onClick={() => navigate("/quotes/new", { replace: true })}
            data-testid="welcome-new-quote"
            className="group h-40 bg-[#4ECCC8] hover:bg-[#3fb8b4] text-[#003a60] rounded-lg p-6 flex flex-col items-start justify-between text-left transition-transform hover:scale-[1.02] shadow-xl">
            <FilePlus2 className="h-10 w-10" strokeWidth={2.2} />
            <div>
              <div className="text-2xl font-black">Nueva cotización</div>
              <div className="text-sm mt-1 opacity-80 flex items-center gap-2">
                Ir directo al editor <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>
          <button
            onClick={() => navigate("/dashboard", { replace: true })}
            data-testid="welcome-dashboard"
            className="group h-40 bg-white/10 hover:bg-white/15 border border-white/25 text-white rounded-lg p-6 flex flex-col items-start justify-between text-left transition-transform hover:scale-[1.02]">
            <LayoutDashboard className="h-10 w-10 text-[#4ECCC8]" strokeWidth={2.2} />
            <div>
              <div className="text-2xl font-black">Dashboard</div>
              <div className="text-sm mt-1 opacity-80 flex items-center gap-2">
                Ver el resumen del día <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>
        </div>

        <button onClick={() => navigate("/dashboard", { replace: true })}
                data-testid="welcome-skip"
                className="mt-10 text-sm text-white/60 hover:text-white/90 underline underline-offset-4">
          Saltar y ver todo el menú
        </button>
      </div>
    </div>
  );
}
